'use client'

import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from 'recharts'
import { BarChart3 } from 'lucide-react'
import { DashboardData, DOC_MODULE_ORDER, MODULE_META } from '../types'

interface Props {
  data: DashboardData
}

export default function StatsSection({ data }: Props) {
  const { activity, documents } = data

  // 1) Activité lifetime par module (source : profiles.*_count)
  const activityBars = useMemo(
    () => [
      { label: 'Transcription', value: activity.transcription, color: MODULE_META.transcription.color },
      { label: 'Codification', value: activity.codification, color: MODULE_META.codification.color },
      { label: 'Rapport', value: activity.rapport, color: MODULE_META.rapport.color },
      { label: 'Note tech.', value: activity.note, color: MODULE_META['note-technique'].color },
      { label: 'Correction', value: activity.correction, color: '#74c69d' },
    ],
    [activity],
  )

  const totalOps = activityBars.reduce((s, b) => s + b.value, 0)

  // 2) Documents créés par semaine (8 dernières) — source : created_at des docs
  const weeklyBars = useMemo(() => {
    const all: string[] = []
    for (const mod of DOC_MODULE_ORDER) {
      for (const d of documents[mod] ?? []) all.push(d.created_at)
    }
    const weeks: { label: string; start: number; value: number }[] = []
    const now = new Date()
    const monday = (d: Date) => {
      const x = new Date(d)
      const day = (x.getDay() + 6) % 7
      x.setHours(0, 0, 0, 0)
      x.setDate(x.getDate() - day)
      return x
    }
    let cursor = monday(now)
    for (let i = 7; i >= 0; i--) {
      const start = new Date(cursor)
      start.setDate(start.getDate() - i * 7)
      const end = new Date(start)
      end.setDate(end.getDate() + 7)
      weeks.push({
        label: start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        start: start.getTime(),
        value: 0,
      })
      for (const iso of all) {
        const t = new Date(iso).getTime()
        if (t >= start.getTime() && t < end.getTime()) weeks[weeks.length - 1].value++
      }
    }
    return weeks
  }, [documents])

  const hasWeekly = weeklyBars.some((w) => w.value > 0)

  if (totalOps === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="h-10 w-10 text-tq-muted mx-auto mb-3 opacity-40" />
        <p className="text-sm text-tq-muted">Pas encore de statistiques.</p>
        <p className="text-xs text-tq-muted mt-1">
          Les données apparaîtront après votre première opération.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Résumé */}
      <div className="rounded-xl border border-tq-border bg-white p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-tq-muted font-medium">Opérations totales</span>
          <span className="text-lg font-bold text-tq-text">{totalOps.toLocaleString('fr-FR')}</span>
        </div>
      </div>

      {/* Activité par module */}
      <div>
        <p className="text-xs font-semibold text-tq-text mb-3 uppercase tracking-wider">
          Répartition de l&apos;activité par module
        </p>
        <div className="rounded-xl border border-tq-border bg-white p-3" style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={activityBars} margin={{ top: 8, right: 8, left: -16, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2ee" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: 'rgba(27,67,50,0.05)' }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {activityBars.map((b, i) => (
                  <Cell key={i} fill={b.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Activité dans le temps */}
      <div>
        <p className="text-xs font-semibold text-tq-text mb-3 uppercase tracking-wider">
          Documents enregistrés (8 dernières semaines)
        </p>
        {hasWeekly ? (
          <div className="rounded-xl border border-tq-border bg-white p-3" style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyBars} margin={{ top: 8, right: 8, left: -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2ee" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(27,67,50,0.05)' }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <Bar dataKey="value" fill="#40916c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-tq-muted rounded-lg border border-tq-border bg-white p-4">
            Aucun document enregistré récemment. Les documents sauvegardés apparaîtront ici.
          </p>
        )}
      </div>
    </div>
  )
}
